import {
    addContainerClassesNoRules,
    multiClasses,
} from '@dat/iq-style-processor';
import {
    Component as ComponentType,
    ComponentTypeValues,
    Section,
} from '../../models';
import { idCreator } from '../../models/builder-utils';
import {
    Controls,
    EquipTypes,
    createSectionClasses,
    createdHeader,
    createdHeaderComponent,
} from './shared.models';
import { ViewTitleSection } from './view-trends-dashboard.models';

const MapComponentTypes: Record<EquipTypes, ComponentTypeValues> = {
    REEFER: 'TrendlinesRatesMapReefer',
    FLATBED: 'TrendlinesRatesMapFlatbed',
    VAN: 'TrendlinesRatesMapVan',
};
const GraphComponentTypes: Record<EquipTypes, ComponentTypeValues> = {
    REEFER: 'TrendlinesRatesGraphReefer',
    FLATBED: 'TrendlinesRatesGraphFlatbed',
    VAN: 'TrendlinesRatesGraphVan',
};
const NationalGraphComponentTypes: Record<EquipTypes, ComponentTypeValues> = {
    REEFER: 'TrendlinesRatesNationalGraphReefer',
    FLATBED: 'TrendlinesRatesNationalGraphFlatbed',
    VAN: 'TrendlinesRatesNationalGraphVan',
};

const NationalRatesTitles: Record<EquipTypes, string> = {
    REEFER: 'National Reefer Rates',
    FLATBED: 'National Flatbed Rates',
    VAN: 'National Van Rates',
};

const RatesTitles: Record<EquipTypes, string> = {
    REEFER: 'Reefer Rates',
    FLATBED: 'Flatbed Rates',
    VAN: 'Van Rates',
};

// #region L/T Map
const RatesMapComponent = (
    idRoot: string,
    type: EquipTypes,
): ComponentType => ({
    id: idCreator(idRoot, ['rates-map']),
    type: MapComponentTypes[type],
    props: {
        id: idCreator(idRoot, ['rates-map']),
        type,
    },
    classNames: {
        ...multiClasses(
            ['w-full', 'mt-10', 'sm:mt-0'],
            ['[data-uitype="builder-component"]'],
        ),
        ...multiClasses(
            ['!h-[500px]', 'sm:!h-[300px]'],
            ['[data-uitype="graph"]'],
        ),
    },
});
// #endregion L/T Map

// #region L/T Graph
const RatesGraphComponent = (
    idRoot: string,
    type: EquipTypes,
): ComponentType => ({
    id: idCreator(idRoot, ['rates-graph']),
    type: GraphComponentTypes[type],
    props: {
        id: idCreator(idRoot, ['rates-graph']),
        type,
    },
    classNames: {
        ...multiClasses(
            ['w-full', 'mb-10', 'w-[84%]', 'm-auto', 'tablet:w-full','tablet:px-10'],
            ['[data-uitype="builder-component"]'],
        ),
        ...multiClasses(['!bg-black', '!h-[500px]'], ['[data-uitype="graph"]']),
        ...multiClasses(['bg-black'], ['[data-uitype="graph-legend"]']),
    },
});
// #endregion L/T Graph

// #region Rates Graph
const RatesNationalGraphComponent = (
    idRoot: string,
    type: EquipTypes,
): ComponentType => ({
    id: idCreator(idRoot, ['rates-graph']),
    type: NationalGraphComponentTypes[type],
    props: {
        id: idCreator(idRoot, ['rates-graph']),
        type,
    },
    classNames: {
        ...multiClasses(['w-full'], ['[data-uitype="builder-component"]']),

        ...addContainerClassesNoRules([
            'col-span-1',
            'px-[16%]',
            'sm:order-4',
            'tablet:order-4',
            'sm:pl-10',
            'tablet:w-full',
            'tablet:px-10',
        ]),
        ...multiClasses(
            ['mp-10', 'w-[84%]', 'm-auto'],
            ['[data-uitype="builder-component"]'],
        ),
        ...multiClasses(['!bg-black', '!h-[500px]'], ['[data-uitype="graph"]']),
        ...multiClasses(['bg-black'], ['[data-uitype="graph-legend"]']),
    },
});
// #endregion Rates Graph

export const RatesSharedSections = (
    idRoot: string,
    type: EquipTypes,
): Section[] => {
    return [
        {
            ...ViewTitleSection,
            classNames: {
                ...addContainerClassesNoRules(['bg-transparent', 'col-span-2']),
                ...multiClasses(
                    ['!bg-transparent', 'max-w-[1140px]', 'mx-auto'],
                    ['[data-uitype="builder-section"]'],
                ),
            },
        },
        {
            id: idCreator(idRoot, ['section', 'map']),
            components: [
                createdHeaderComponent(idRoot, type, NationalRatesTitles[type]),
                Controls(
                    idCreator(idRoot, ['section', 'map', 'controls']),
                    `/trendlines/${type.toLowerCase()}/national-rates`,
                ),
                RatesMapComponent(
                    idCreator(idRoot, ['section', 'map', 'viz']),
                    type,
                ),
                {
                    id: idCreator(idRoot, ['section', 'map', 'label']),
                    type: 'Label',
                    props: {
                        id: idCreator(idRoot, ['section', 'map', 'label']),
                        label: `The map above shows the previous week’s average outbound ${type?.toLowerCase()} rate for each region.`,
                    },
                    classNames: {
                        ...multiClasses(
                            [
                                'w-full',
                                'text-white',
                                'font-caption',
                                'my-10',
                                'italic',
                                'sm:px-10',
                                'tablet:px-10',
                            ],
                            ['[data-uitype="builder-component"]'],
                        ),
                        ...multiClasses(
                            [
                                'no-underline',
                                'hover:underline',
                                'text-dat-blue-500',
                            ],
                            ['a'],
                        ),
                        ...multiClasses(['tl-graph-title'], ['label']),
                    },
                },
                {
                    id: idCreator(idRoot, [
                        'section',
                        'map',
                        'disclaimer',
                        'label',
                    ]),
                    type: 'Label',
                    props: {
                        id: idCreator(idRoot, ['section', 'map', 'label']),
                        label: `Check the <a href="https://www.dat.com/blog" target="_parent">**DAT Freight Talk blog**</a> for regular updates on market conditions,
                        regulations and industry trends.`,
                    },
                    classNames: {
                        ...multiClasses(
                            ['w-full', 'text-white', 'sm:px-10','tablet:px-10'],
                            ['[data-uitype="builder-component"]'],
                        ),
                        ...multiClasses(
                            [
                                'no-underline',
                                'hover:underline',
                                'text-dat-blue-500',
                            ],
                            ['a'],
                        ),
                        ...multiClasses(['tl-map-title'], ['label']),
                    },
                },
            ],
            classNames: { ...createSectionClasses(2, false) },
        },
        {
            id: idCreator(idRoot, ['section', 'rates-upsell']),
            components: [
                {
                    id: idCreator(idRoot, [
                        'section',
                        'rates-upsell',
                        'banner',
                    ]),
                    type: 'TrendlinesUpsellBanner',
                    props: {
                        id: idCreator(idRoot, [
                            'section',
                            'rates-upsell',
                            'banner',
                        ]),
                        type: 'RateView',
                        equipmentType: type,
                    },
                    cssClassRules: [
                        {
                            classNames: ['text-black', 'w-full'],
                            cssSelectors: [''],
                        },
                    ],
                },
            ],
            classNames: {
                ...createSectionClasses(2, true),
                ...multiClasses(
                    ['!pb-[100px]', 'sm:pb-[50px]', '!pt-0'],
                    ['[data-uitype="builder-section"]'],
                ),
            },
        },
        {
            id: idCreator(idRoot, ['section', 'rates-graph']),
            components: [
                createdHeaderComponent(idRoot, type, RatesTitles[type]),
                RatesGraphComponent(
                    idCreator(idRoot, ['section', 'rates-graph', 'viz']),
                    type,
                ),
                {
                    id: idCreator(
                        idCreator(idRoot, ['section', 'rates-graph']),
                        ['graph-description'],
                    ),
                    classNames: {
                        ...multiClasses(
                            [
                                'w-full',
                                'text-white',
                                'font-caption-micro',
                                'italic',
                                'sm:px-10',
                                'sm:order-5',
                                'tablet:px-10'
                            ],
                            ['[data-uitype="builder-component"]'],
                        ),
                        ...multiClasses(
                            [
                                'no-underline',
                                'hover:underline',
                                'text-dat-blue-500',
                            ],
                            ['a'],
                        ),
                        ...multiClasses(['tl-graph-title'], ['label']),
                    },
                    type: 'Label',
                    props: {
                        id: idCreator(
                            idCreator(
                                idCreator(idRoot, ['section', 'rates-graph']),
                                ['graph-title'],
                            ),
                            ['description'],
                        ),
                        label: `The chart above depicts national average rates (including fuel surcharges) in the past 13 months, derived from <a href="https://www.dat.com/freight-rates" target="_parent">DAT RateView</a>.`,
                    },
                },
            ],
            classNames: {
                ...createSectionClasses(2, true),
                ...multiClasses(['inline-block'], [`[data-uitype="label"]`]),
            },
        },
        {
            id: idCreator(idRoot, ['section', 'rates-national-graph']),
            title: '',

            components: [
                {
                    id: idCreator(
                        idCreator(idRoot, ['section', 'rates-national-graph']),
                        ['about-title'],
                    ),
                    type: 'Label',
                    classNames: {
                        ...addContainerClassesNoRules([
                            'col-span-1',
                            'px-[16%]',
                            'sm:order-1',
                            'tablet:order-1',
                            'sm:px-2',
                            'tablet:px-[25px]',
                            'tablet:m-0',
                            'sm:mb-1',
                        ]),
                    },
                    props: {
                        id: idCreator(
                            idCreator(
                                idCreator(idRoot, [
                                    'section',
                                    'rates-national-graph',
                                ]),
                                ['about-title'],
                            ),
                            ['title'],
                        ),
                        label: createdHeader(`About Market Rates`),
                    },
                },
                {
                    id: idCreator(
                        idCreator(idRoot, ['section', 'rates-graph']),
                        ['graph-title'],
                    ),
                    classNames: {
                        ...addContainerClassesNoRules([
                            'col-span-1',
                            'px-[16%]',
                            'sm:order-3',
                            'sm:m-0',
                            'sm:pl-2',
                            'tablet:order-3',
                            'tablet:m-0',
                            'tablet:px-[25px]',
                            'tablet:w-full',
                        ]),
                    },
                    type: 'Label',
                    props: {
                        id: idCreator(
                            idCreator(
                                idCreator(idRoot, [
                                    'section',
                                    'rates-national-graph',
                                ]),
                                ['graph-title'],
                            ),
                            ['title'],
                        ),
                        label: createdHeader(`National Averages`),
                    },
                },
                {
                    id: idCreator(
                        idCreator(idRoot, ['section', 'rates-national-graph']),
                        ['about-description'],
                    ),
                    classNames: {
                        ...addContainerClassesNoRules([
                            'col-span-1',
                            'px-[16%]',
                            'sm:order-2',
                            'sm:pl-10',
                            'text-pretty',
                            'sm:pb-10',
                            'tablet:order-2',
                            'tablet:px-10',
                            'tablet:pb-10',
                            'tablet:w-full',
                        ]),
                        ...multiClasses(
                            [
                                'no-underline',
                                'hover:underline',
                                'text-dat-blue-500',
                            ],
                            ['a'],
                        ),
                        ...multiClasses(['tl-description'], ['label']),
                    },
                    type: 'Label',
                    props: {
                        id: idCreator(
                            idCreator(
                                idCreator(idRoot, [
                                    'section',
                                    'rates-national-graph',
                                ]),
                                ['about-description'],
                            ),
                            ['description'],
                        ),
                        label: `<span class="italic">Rates are market averages derived from </span> <a href="https://www.dat.com/freight-rates" target="_parent">DAT RateView™</a><span class="italic">,
an innovative service that provides real-time reports on prevailing spot market and contract rates,
as well as historical rate and capacity trends. RateView's comprehensive database is comprised of $1 trillion in freight invoices for any lane.</span>`,
                    },
                },
                RatesNationalGraphComponent(
                    idCreator(idRoot, [
                        'section',
                        'rates-national-graph',
                        'viz',
                    ]),
                    type,
                ),
            ],
            classNames: {
                ...createSectionClasses(2, false),
                ...multiClasses(
                    [
                        'grid',
                        'grid-cols-2',
                        'sm:grid-cols-1',
                        'tablet:grid-cols-1',
                        'gap-x-2',
                        'gap-y-4',
                        'mb-[50px]',
                        'mt-[50px]',
                    ],
                    ['[data-uitype="builder-section"]'],
                ),
            },
        },
    ];
};
